import React from "react";
import SEO from "../components/SEO";

const AbstractSubmission = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-16 pt-28 px-4 sm:px-6 lg:px-8">
      <SEO title="Abstract Submission" />
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#1e1b4b] mb-4 tracking-tight">
            Abstract Submission
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Welcome to the abstract submission portal for NCLC-2026. Please
            review the guidelines carefully before submitting your work.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          {/* Quick Links Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-[#1e1b4b] mb-6 flex items-center">
              <svg
                className="w-6 h-6 mr-3 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              Quick Links
            </h2>

            <div className="space-y-6">
              <div className="group">
                <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                  Conference Website
                </p>
                <a
                  href="https://www.sharda.ac.in/nclc-2026/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium transition-colors text-lg break-all"
                >
                  https://www.sharda.ac.in/nclc-2026/
                  <svg
                    className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>

              <div className="w-full h-px bg-gray-100"></div>

              <div className="group">
                <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                  Abstract Submission Link
                </p>
                <a
                  href="https://forms.gle/Hm6jcFvM3UrgvQQp6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  Submit Abstract Here
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Submission Guidelines - Value Add */}
          <div className="bg-gradient-to-br from-[#1e1b4b] to-indigo-900 rounded-2xl shadow-md p-8 text-white">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <svg
                className="w-6 h-6 mr-3 text-indigo-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Submission Guidelines
            </h2>
            <ul className="space-y-4 text-indigo-100">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 text-indigo-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                Abstracts must be written in English and adhere to the provided
                template.
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 text-indigo-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                Clearly state the names, affiliations, and contact details of
                all authors.
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 text-indigo-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                The word limit for the abstract is typically 300-500 words.
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 text-indigo-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                Ensure submission before the final deadline to be considered for
                presentation.
              </li>
            </ul>
          </div>
        </div>

        {/* QR Codes Section */}
        {/* <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-indigo-600 inline-block">
              Scan for Access
            </h2>
            <div className="w-24 h-1 bg-gray-200 mx-auto mt-4 rounded-full"></div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24">
            <div className="flex flex-col items-center group">
              <div className="p-4 bg-white rounded-xl shadow-md border border-gray-100 mb-6 transform transition duration-300 group-hover:-translate-y-2 group-hover:shadow-xl">
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=https://forms.gle/jNLUq3qKvrRuq922A"
                  alt="Abstract Submission QR"
                  className="w-48 h-48 md:w-56 md:h-56 object-contain"
                />
              </div>
              <span className="text-xl font-bold text-indigo-700">
                Abstract Submission link!
              </span>
            </div>

            <div className="hidden md:flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-inner">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>
            </div>

            <div className="flex flex-col items-center group">
              <div className="p-4 bg-white rounded-xl shadow-md border border-gray-100 mb-6 transform transition duration-300 group-hover:-translate-y-2 group-hover:shadow-xl">
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=UPI_PAYMENT_LINK_PLACEHOLDER"
                  alt="UPI Payment QR"
                  className="w-48 h-48 md:w-56 md:h-56 object-contain"
                />
              </div>
              <span className="text-xl font-bold text-indigo-700">
                UPI payment link!
              </span>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AbstractSubmission;
